import React, { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import { UtilityButton } from "components/ds";

interface SidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

// Deep layered "floating" shadow (ambient + mid + contact + hairline ring) so
// the inset panel reads as hovering above the dimmed shell. Matches design.tsx.
const ELEVATED_SHADOW =
  "0 24px 48px rgba(0, 0, 0, 0.18), 0 12px 20px rgba(0, 0, 0, 0.10), 0 4px 8px rgba(0, 0, 0, 0.06), 0 0 0 0.5px rgba(0, 0, 0, 0.06)";

/**
 * Sidepanel — a floating card that slides in from the right over a white,
 * blurred scrim (PLO-4803). Inset 12px from the top/right/bottom, ~440px on md
 * and 45% on lg, 12px radius. Click the scrim to close. Close button pinned
 * top-right inside the panel.
 */
const Sidepanel: React.FC<SidepanelProps> = ({ isOpen, onClose, children }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Double RAF so the browser paints the closed state before we animate in.
      let rafId1: number;
      let rafId2: number;
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      return () => {
        cancelAnimationFrame(rafId1);
        cancelAnimationFrame(rafId2);
      };
    } else {
      setIsAnimating(false);
      // Keep mounted through the (0.45s) slide-out before unmounting.
      const timer = setTimeout(() => setShouldRender(false), 460);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <>
      {/* Backdrop — white 40% scrim + 4px blur over the whole shell. */}
      <Box
        position="fixed"
        inset={0}
        zIndex={1400}
        bg="rgba(255, 255, 255, 0.4)"
        sx={{ backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
        onClick={onClose}
        opacity={isAnimating ? 1 : 0}
        transition="opacity 0.45s cubic-bezier(0.65, 0, 0.35, 1)"
        pointerEvents={isAnimating ? "auto" : "none"}
      />

      {/* Floating panel — inset 12px, slides in from the right edge. */}
      <Box
        position="fixed"
        top="12px"
        right="12px"
        bottom="12px"
        width={{ base: "calc(100% - 24px)", md: "440px", lg: "45%" }}
        bg="bg.container"
        zIndex={1500}
        borderRadius="component.2xl"
        boxShadow={ELEVATED_SHADOW}
        transform={isAnimating ? "translateX(0)" : "translateX(calc(100% + 16px))"}
        transition="transform 0.45s cubic-bezier(0.65, 0, 0.35, 1)"
        overflowY="auto"
        display="flex"
        flexDirection="column"
      >
        {/* Close — small subtle icon button pinned top-right. */}
        <Box position="absolute" top="20px" right="20px" zIndex={2}>
          <UtilityButton
            iconOnly
            size="md"
            treatment="subtle"
            sentiment="neutral"
            aria-label="Close"
            onClick={onClose}
            leftIcon={<FiX size={16} />}
          />
        </Box>
        {children}
      </Box>
    </>
  );
};

export default Sidepanel;
